import type { Notebook } from "@ronin/contracts";
import { cn } from "@ronin/ui";
import {
  ChevronRight,
  FileText,
  Folder,
  FolderOpen,
  FolderPlus,
  Pin,
  Plus,
  TextCursorInput,
  Trash2,
} from "lucide-react";
import {
  Fragment,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/** What the middle pane shows: a fixed view or one notebook. */
export type NotesSelection =
  | { view: "all" }
  | { view: "pinned" }
  | { view: "trash" }
  | { view: "notebook"; notebookId: string };

export interface TreeNode {
  notebook: Notebook;
  depth: number;
  children: TreeNode[];
}

/** Flat notebooks → sorted tree. */
export function buildTree(notebooks: readonly Notebook[]): TreeNode[] {
  const byParent = new Map<string | null, Notebook[]>();
  for (const n of notebooks) byParent.set(n.parentId, [...(byParent.get(n.parentId) ?? []), n]);
  const build = (parentId: string | null, depth: number): TreeNode[] =>
    (byParent.get(parentId) ?? [])
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((notebook) => ({ notebook, depth, children: build(notebook.id, depth + 1) }));
  return build(null, 1);
}

const EXPANDED_KEY = "ronin.notes.expanded";

/** Open notebooks are remembered per device (spec04 FR5). */
function loadExpanded(): Set<string> {
  try {
    const raw = localStorage.getItem(EXPANDED_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function saveExpanded(ids: Set<string>) {
  try {
    localStorage.setItem(EXPANDED_KEY, JSON.stringify([...ids]));
  } catch {
    // Storage may be unavailable (private mode); then it is just not remembered.
  }
}

interface Row {
  key: string;
  selection: NotesSelection;
  label: string;
  depth: number;
  icon: typeof Folder;
  notebook?: Notebook;
  expandable?: boolean;
  expanded?: boolean;
  parentKey?: string;
}

interface NotebookTreeProps {
  notebooks: readonly Notebook[];
  selection: NotesSelection;
  onSelect: (selection: NotesSelection) => void;
  onNewNotebook: () => void;
  canAddSubNotebook: (notebook: Notebook) => boolean;
  onNewSubNotebook: (notebook: Notebook) => void;
  onRenameNotebook: (notebook: Notebook) => void;
  onDeleteNotebook: (notebook: Notebook) => void;
}

const keyOf = (s: NotesSelection) => (s.view === "notebook" ? s.notebookId : s.view);

/**
 * Left pane (spec04 FR3, FR19): All notes, Pinned, Trash, a divider, then the notebook tree.
 * A proper ARIA tree: arrow keys move, → opens, ← closes or goes to the parent, Enter
 * selects, F2 renames and Delete deletes a notebook. A notebook row shows its actions on
 * the right while selected or hovered.
 */
export function NotebookTree(props: NotebookTreeProps) {
  const { notebooks, selection, onSelect, onNewNotebook } = props;
  const [expanded, setExpanded] = useState(loadExpanded);
  const [focusKey, setFocusKey] = useState(keyOf(selection));
  const refs = useRef(new Map<string, HTMLDivElement>());
  const tree = useMemo(() => buildTree(notebooks), [notebooks]);
  const selectedId = selection.view === "notebook" ? selection.notebookId : null;

  // A selected notebook is always visible: its parents open (e.g. a new sub-notebook).
  useEffect(() => {
    const byId = new Map(notebooks.map((n) => [n.id, n]));
    const parents: string[] = [];
    let p = selectedId ? byId.get(selectedId)?.parentId : null;
    // Bounded, so a broken parent chain cannot loop.
    while (p && parents.length < notebooks.length) {
      parents.push(p);
      p = byId.get(p)?.parentId;
    }
    if (parents.length === 0) return;
    setExpanded((current) => {
      if (parents.every((id) => current.has(id))) return current;
      const next = new Set([...current, ...parents]);
      saveExpanded(next);
      return next;
    });
  }, [selectedId, notebooks]);

  const toggle = (id: string, open?: boolean) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (open ?? !next.has(id)) next.add(id);
      else next.delete(id);
      saveExpanded(next);
      return next;
    });

  const rows: Row[] = [
    { key: "all", selection: { view: "all" }, label: "All notes", depth: 1, icon: FileText },
    { key: "pinned", selection: { view: "pinned" }, label: "Pinned", depth: 1, icon: Pin },
    { key: "trash", selection: { view: "trash" }, label: "Trash", depth: 1, icon: Trash2 },
  ];
  const firstNotebook = rows.length;
  const visit = (nodes: TreeNode[], parentKey?: string) => {
    for (const node of nodes) {
      const id = node.notebook.id;
      const open = expanded.has(id);
      rows.push({
        key: id,
        selection: { view: "notebook", notebookId: id },
        label: node.notebook.name,
        depth: node.depth,
        icon: open && node.children.length > 0 ? FolderOpen : Folder,
        notebook: node.notebook,
        expandable: node.children.length > 0,
        expanded: open,
        parentKey,
      });
      if (open) visit(node.children, id);
    }
  };
  visit(tree);

  const selectedKey = keyOf(selection);
  const tabKey = rows.some((r) => r.key === focusKey) ? focusKey : selectedKey;

  function move(key: string) {
    setFocusKey(key);
    refs.current.get(key)?.focus();
  }

  function onKeyDown(event: KeyboardEvent, row: Row, index: number) {
    const target = (() => {
      switch (event.key) {
        case "ArrowDown":
          return rows[index + 1]?.key;
        case "ArrowUp":
          return rows[index - 1]?.key;
        case "Home":
          return rows[0]?.key;
        case "End":
          return rows[rows.length - 1]?.key;
        case "ArrowRight":
          if (row.expandable && !row.expanded) toggle(row.key, true);
          else if (row.expandable) return rows[index + 1]?.key;
          return null;
        case "ArrowLeft":
          if (row.expandable && row.expanded) toggle(row.key, false);
          else return row.parentKey;
          return null;
        case "Enter":
        case " ":
          onSelect(row.selection);
          return null;
        case "F2":
          if (row.notebook) props.onRenameNotebook(row.notebook);
          return null;
        case "Delete":
          if (row.notebook) props.onDeleteNotebook(row.notebook);
          return null;
        default:
          return undefined;
      }
    })();
    if (
      target === undefined &&
      !["ArrowDown", "ArrowUp", "Home", "End", "ArrowLeft"].includes(event.key)
    ) {
      return;
    }
    event.preventDefault();
    if (target) move(target);
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-11 shrink-0 items-center justify-between border-b px-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-dim-foreground">
          Notebooks
        </span>
        <button
          type="button"
          onClick={onNewNotebook}
          aria-label="New notebook"
          title="New notebook"
          className="flex size-7 items-center justify-center text-dim-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
        >
          <Plus className="size-4" />
        </button>
      </div>
      <div role="tree" aria-label="Notebooks" className="min-h-0 flex-1 overflow-y-auto p-2">
        {rows.map((row, index) => {
          const Icon = row.icon;
          const selected = row.key === selectedKey;
          return (
            <Fragment key={row.key}>
              {index === firstNotebook && <div aria-hidden className="mx-2 my-2 h-px bg-border" />}
              <div
                ref={(el) => {
                  if (el) refs.current.set(row.key, el);
                  else refs.current.delete(row.key);
                }}
                role="treeitem"
                aria-label={row.label}
                aria-level={row.depth}
                aria-selected={selected}
                aria-expanded={row.expandable ? row.expanded : undefined}
                tabIndex={row.key === tabKey ? 0 : -1}
                onClick={() => {
                  setFocusKey(row.key);
                  onSelect(row.selection);
                }}
                onKeyDown={(event) => onKeyDown(event, row, index)}
                className={cn(
                  "group relative flex h-8 pointer-coarse:h-11 cursor-pointer items-center gap-1.5 pr-2 text-xs font-medium outline-none transition-colors duration-150 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
                  selected
                    ? "bg-muted text-foreground before:absolute before:inset-y-1.5 before:left-0 before:w-0.5 before:bg-ring [&_svg.icon]:text-brand"
                    : "text-dim-foreground hover:bg-muted hover:text-secondary-foreground",
                )}
                style={{ paddingLeft: 8 + (row.depth - 1) * 14 }}
              >
                {row.expandable ? (
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-hidden
                    onClick={(event) => {
                      event.stopPropagation();
                      toggle(row.key);
                    }}
                    className="flex size-4 pointer-coarse:size-8 shrink-0 items-center justify-center text-faint-foreground hover:text-foreground"
                  >
                    <ChevronRight
                      className={cn(
                        "size-3.5 transition-transform duration-150",
                        row.expanded && "rotate-90",
                      )}
                    />
                  </button>
                ) : (
                  <span className="size-4 shrink-0" />
                )}
                <Icon className="icon size-3.5 shrink-0" aria-hidden />
                <span className="min-w-0 flex-1 truncate">{row.label}</span>
                {row.notebook && (
                  <NotebookActions
                    notebook={row.notebook}
                    visible={selected}
                    canAddSub={props.canAddSubNotebook(row.notebook)}
                    onNewSub={props.onNewSubNotebook}
                    onRename={props.onRenameNotebook}
                    onDelete={props.onDeleteNotebook}
                  />
                )}
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

interface NotebookActionsProps {
  notebook: Notebook;
  /** Always shown on the selected row; on others while hovered or focused. */
  visible: boolean;
  canAddSub: boolean;
  onNewSub: (notebook: Notebook) => void;
  onRename: (notebook: Notebook) => void;
  onDelete: (notebook: Notebook) => void;
}

/**
 * Sub-notebook, rename and delete on the notebook's row. Out of the tab order (the tree
 * has one tab stop; F2 and Delete are the keys). Delete stands apart after a divider.
 */
function NotebookActions({
  notebook,
  visible,
  canAddSub,
  onNewSub,
  onRename,
  onDelete,
}: NotebookActionsProps) {
  const action = (label: string, run: (n: Notebook) => void, icon: ReactNode, danger = false) => (
    <button
      type="button"
      tabIndex={-1}
      aria-label={`${label} ${notebook.name}`}
      title={label}
      onClick={(event) => {
        event.stopPropagation();
        run(notebook);
      }}
      className={cn(
        "flex size-6 pointer-coarse:size-11 shrink-0 items-center justify-center text-faint-foreground transition-colors duration-150 [&_svg]:size-3.5",
        danger ? "hover:text-destructive" : "hover:text-foreground",
      )}
    >
      {icon}
    </button>
  );
  return (
    <span
      className={cn(
        "-mr-1 shrink-0 items-center",
        visible ? "flex" : "hidden group-hover:flex group-focus-within:flex",
      )}
    >
      {canAddSub && action("New sub-notebook in", onNewSub, <FolderPlus />)}
      {action("Rename", onRename, <TextCursorInput />)}
      <span aria-hidden className="mx-1 h-4 w-px bg-border" />
      {action("Delete", onDelete, <Trash2 />, true)}
    </span>
  );
}
